import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Eye, Heart, Radio, Trophy, Clock, ChevronDown, HelpCircle } from "lucide-react";
import MemberModal from "@/components/community/MemberModal";
import StreamStage from "@/components/community/StreamStage";
import { PlatformBadge, PLATFORMS } from "@/components/community/StreamEmbed";
import { useAuth } from "@/context/AuthContext";

export function streamerBadge(s) {
  if (s.rank === 1) return { label: "Top Streamer", cls: "bg-yellow-400/15 text-yellow-300 border-yellow-400/30" };
  if ((s.likes || 0) >= 1000) return { label: `${Math.floor((s.likes || 0) / 1000)}K Likes`, cls: "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30" };
  if ((s.likes || 0) >= 100) return { label: "Rising", cls: "bg-white/10 text-white/70 border-white/15" };
  return null;
}

function StreamThumb({ s, onClick, active }) {
  return (
    <button onClick={onClick} data-testid={`live-thumb-${s.id}`}
      className={`group text-left card-surface rounded-xl overflow-hidden hover-rise ${active ? "ring-2 ring-[hsl(var(--primary))]" : ""}`}>
      <div className="relative aspect-video bg-black overflow-hidden">
        {s.streamer.avatar ? <img src={mediaUrl(s.streamer.avatar)} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition" />
          : <div className="w-full h-full grid place-items-center text-white/20"><Radio size={30} /></div>}
        <div className="absolute top-2 left-2"><PlatformBadge platform={s.platform} /></div>
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold"><span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" /> {s.viewers.toLocaleString()}</span>
      </div>
      <div className="p-2.5 flex items-center gap-2">
        {s.streamer.avatar && <img src={mediaUrl(s.streamer.avatar)} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate">{s.streamer.nickname || s.streamer.name}</div>
          <div className="text-xs text-white/40 truncate">{s.game}</div>
        </div>
      </div>
    </button>
  );
}

function SoonThumb({ u, onClick }) {
  return (
    <button onClick={onClick} data-testid={`soon-thumb-${u.user_id}`} className="group text-left card-surface rounded-xl overflow-hidden hover-rise">
      <div className="relative aspect-video bg-black overflow-hidden">
        {u.avatar ? <img src={mediaUrl(u.avatar)} alt="" className="w-full h-full object-cover opacity-40 grayscale group-hover:opacity-60 transition" />
          : <div className="w-full h-full grid place-items-center text-white/15"><Radio size={30} /></div>}
        <div className="absolute top-2 left-2"><PlatformBadge platform={u.platform} /></div>
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-black/70 text-white/80 text-[10px] font-bold uppercase tracking-wide" data-testid="streaming-soon-label"><Clock size={10} /> Streaming soon</span>
      </div>
      <div className="p-2.5 min-w-0">
        <div className="text-sm font-semibold text-white truncate">{u.nickname || "Streamer"}</div>
        <div className="text-xs text-white/40 truncate">{u.game || PLATFORMS[u.platform]?.label}</div>
      </div>
    </button>
  );
}

function EmptySlot({ i }) {
  return (
    <div data-testid={`stream-slot-empty-${i}`} className="rounded-xl border border-dashed border-white/10 overflow-hidden">
      <div className="aspect-video grid place-items-center text-white/15"><Radio size={24} /></div>
      <div className="p-2.5 text-xs text-white/25">Slot available</div>
    </div>
  );
}

function GameFilters({ games, value, onChange }) {
  const [open, setOpen] = useState(false);
  const shown = games.slice(0, 5);
  const rest = games.slice(5);
  const pill = (on) => `text-sm font-semibold px-4 py-1.5 rounded-full border transition ${on ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : "border-white/15 text-white/60 hover:text-white"}`;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="stream-filters">
      {["", ...shown].map((g) => (
        <button key={g || "all"} onClick={() => onChange(g)} data-testid={`stream-filter-${g || "all"}`} className={pill(value === g)}>{g || "All"}</button>
      ))}
      {rest.length > 0 && (
        <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
          <button onClick={() => setOpen(!open)} data-testid="stream-filter-more" className={`${pill(rest.includes(value))} inline-flex items-center gap-1`}>
            {rest.includes(value) ? value : "More"} <ChevronDown size={14} />
          </button>
          {open && (
            <div className="absolute left-0 mt-2 w-56 glass rounded-xl p-1 z-40" data-testid="stream-filter-more-menu">
              {rest.map((g) => (
                <button key={g} onClick={() => { onChange(g); setOpen(false); }} data-testid={`stream-filter-${g}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${value === g ? "text-[hsl(var(--primary))] font-bold" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>{g}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ title, to, children, testid }) {
  return (
    <section className="card-surface rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-white">{title}</h3>
        {to && <Link to={to} className="text-xs font-semibold text-[hsl(var(--primary))]">View all</Link>}
      </div>
      {children}
    </section>
  );
}

export default function Esports() {
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [games, setGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [soon, setSoon] = useState([]);
  const [member, setMember] = useState(null);
  const closeMember = useCallback(() => setMember(null), []);
  const [filter, setFilter] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [top, setTop] = useState([]);
  const [hot, setHot] = useState([]);
  const [tours, setTours] = useState([]);
  const [params] = useSearchParams();
  useEffect(() => { if (params.get("stream")) setActiveId(params.get("stream")); if (params.get("member")) setMember(params.get("member")); }, [params]);
  const loadStreams = (g) => api.get("/streams/live", { params: g ? { game: g } : {} }).then((r) => { setStreams(r.data.items); setGames(r.data.games); }).catch(() => setStreams([]));

  useEffect(() => {
    loadStreams(filter);
    api.get("/streams/soon", { params: { limit: 6, ...(filter ? { game: filter } : {}) } }).then((r) => setSoon(r.data.items)).catch(() => setSoon([]));
  }, [filter, user?.user_id]);
  useEffect(() => {
    api.get("/streams/top", { params: { limit: 5 } }).then((r) => setTop(r.data.items)).catch(() => setTop([]));
    api.get("/news", { params: { sort: "views", limit: 5 } }).then((r) => setHot(r.data.items || [])).catch(() => setHot([]));
    api.get("/tournaments", { params: { limit: 4 } }).then((r) => setTours(r.data.items || [])).catch(() => setTours([]));
    api.get("/games").then((r) => setAllGames((r.data.items || []).map((g) => g.name).filter(Boolean))).catch(() => setAllGames([]));
  }, []);
  const gameList = useMemo(() => {
    const seen = new Set();
    return [...allGames, ...games].filter((g) => { const k = g.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [allGames, games]);

  const active = useMemo(() => streams.find((s) => s.id === activeId) || streams[0], [streams, activeId]);
  const more = streams.filter((s) => s.id !== active?.id).slice(0, 6);
  const soonFill = soon.slice(0, 6 - more.length);
  const emptyCount = 6 - more.length - soonFill.length;
  const onStat = (id, data) => setStreams((cur) => cur.map((s) => (s.id === id ? { ...s, viewers: data.viewers, likes: data.likes } : s)));

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 pt-6 md:pt-8 pb-16">
      {member && <MemberModal userId={member} onClose={closeMember} />}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <GameFilters games={gameList} value={filter} onChange={setFilter} />
        <Link to="/help#live-streaming" data-testid="community-howto-link" className="inline-flex items-center gap-1.5 py-1.5 text-sm font-semibold text-white/60 hover:text-white"><HelpCircle size={15} /> How to go live &amp; connect channels</Link>
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-8 min-w-0">
          <section data-testid="featured-stream">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Radio size={18} className="text-[hsl(var(--primary))]" /> Featured Live Stream</h2>
            {active ? <StreamStage key={active.id} stream={active} onStat={onStat} /> : (
              <div className="card-surface rounded-2xl p-10 text-center text-white/50" data-testid="no-live">
                <Radio size={28} className="mx-auto mb-3 text-white/30" />
                No one is live right now. {user ? <Link to="/profile" className="text-[hsl(var(--primary))] font-semibold">Go live from your profile →</Link> : "Sign in and go live from your profile!"}
              </div>
            )}
          </section>

          <section data-testid="more-streams">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" /> More Streaming <span className="text-xs font-semibold text-white/40">({more.length} live)</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {more.map((s) => <StreamThumb key={s.id} s={s} onClick={() => { setActiveId(s.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}
              {soonFill.map((u) => <SoonThumb key={u.user_id} u={u} onClick={() => setMember(u.user_id)} />)}
              {Array.from({ length: emptyCount }).map((_, i) => <EmptySlot key={i} i={i} />)}
            </div>
          </section>

          <section data-testid="live-tournaments">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Trophy size={18} className="text-[hsl(var(--primary))]" /> Live &amp; Upcoming Tournaments</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {tours.slice(0, 4).map((t) => (
                <Link key={t.slug} to={`/tournaments/${t.slug}`} className="card-surface rounded-2xl p-4 hover-rise flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${["live", "ongoing"].includes(t.status) ? "bg-red-600 text-white live-dot" : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"}`}>{["live", "ongoing"].includes(t.status) ? "Live" : "Upcoming"}</span>
                  <div className="min-w-0"><div className="font-bold text-white truncate">{t.name}</div><div className="text-xs text-white/40 truncate">{t.game}</div></div>
                </Link>
              ))}
              {tours.length === 0 && <p className="text-sm text-white/40">No tournaments yet.</p>}
            </div>
          </section>

        </div>

        <aside className="space-y-6">
          <Panel title="Top Streamer" testid="top-streamers">
            {top.length === 0 ? <p className="text-sm text-white/40">No live streamers yet.</p> : (
              <div className="space-y-1">
                {top.map((s) => (
                  <button key={s.id} onClick={() => setActiveId(s.id)} data-testid={`top-streamer-${s.id}`} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition text-left">
                    <span className="w-5 text-center font-bold text-white/40">{s.rank}</span>
                    {s.streamer.avatar ? <img src={mediaUrl(s.streamer.avatar)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-xs font-bold text-[hsl(var(--primary-foreground))]">{(s.streamer.nickname || "?")[0]}</span>}
                    <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-white truncate flex items-center gap-1.5">{s.streamer.nickname || s.streamer.name}{streamerBadge(s) && <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${streamerBadge(s).cls}`}>{streamerBadge(s).label}</span>}</div><div className="text-xs text-white/40 truncate">{s.game}</div></div>
                    <span className="inline-flex items-center gap-1 text-xs text-white/60"><Heart size={12} className="text-red-400" /> {s.likes >= 1000 ? `${(s.likes / 1000).toFixed(0)}K` : s.likes}</span>
                  </button>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Hot Topics & News" to="/news" testid="hot-topics">
            <div className="space-y-3">
              {hot.map((a, i) => (
                <Link key={a.id} to={`/news/${a.slug}`} data-testid={`hot-topic-${a.slug}`} className="flex gap-3 group">
                  <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0 relative">
                    <img src={mediaUrl(a.cover)} alt="" className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 w-4 h-4 rounded bg-[hsl(var(--primary))] grid place-items-center text-[9px] font-black text-[hsl(var(--primary-foreground))]">{i + 1}</span>
                  </div>
                  <div className="min-w-0"><h4 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-[hsl(var(--primary))]">{a.title}</h4><div className="text-[11px] text-white/40 mt-0.5 flex items-center gap-1"><Eye size={10} /> {a.views?.toLocaleString() || 0}</div></div>
                </Link>
              ))}
              {hot.length === 0 && <p className="text-sm text-white/40">No news yet.</p>}
            </div>
          </Panel>

        </aside>
      </div>
    </div>
  );
}
